import * as THREE from "three";

/** @formigen/geometry — shared helpers.
 *  Conventions: metres, +y up, mount point at the local origin, main axis +y.
 *  Generators hold cosmetic constants only (segment counts); every engineering
 *  number arrives through `params` from the Design Model. */

export const DEG = Math.PI / 180;
export const V3 = (x, y, z) => new THREE.Vector3(x, y, z);
export const UP = V3(0, 1, 0);
export const G = () => new THREE.Group();

export const BX = (w, h, d) => new THREE.BoxGeometry(w, h, d);
export const CY = (r, h, s = 24, rb, open = false) =>
  new THREE.CylinderGeometry(r, rb === undefined ? r : rb, h, s, 1, open);

export const mesh = (geo, mat, x = 0, y = 0, z = 0) => {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  return m;
};

/** A cylinder spanning two points. */
export const rod = (a, b, r, mat, seg = 12) => {
  const d = new THREE.Vector3().subVectors(b, a);
  const m = mesh(CY(r, d.length(), seg), mat);
  m.position.copy(a).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(UP, d.clone().normalize());
  return m;
};

/** Polyline of rods with spherical elbows. */
export const pipe = (g, pts, r, mat) => {
  const v = pts.map((p) => V3(p[0], p[1], p[2]));
  for (let i = 0; i < v.length - 1; i++) g.add(rod(v[i], v[i + 1], r, mat));
  for (let i = 1; i < v.length - 1; i++)
    g.add(mesh(new THREE.SphereGeometry(r * 1.05, 12, 8), mat, v[i].x, v[i].y, v[i].z));
  return g;
};

/** Re-aim a +y mesh along x or z. */
export const along = (m, axis) => {
  if (axis === "x") m.rotation.z = Math.PI / 2;
  else if (axis === "z") m.rotation.x = Math.PI / 2;
  return m;
};

/** Hollow tube (winding, sleeve) built as a lathe so there is no inner seam. */
export const latheTube = (ri, ro, y0, y1, mat, segments = 56) => {
  const pts = [
    new THREE.Vector2(ri, y0), new THREE.Vector2(ro, y0),
    new THREE.Vector2(ro, y1), new THREE.Vector2(ri, y1),
    new THREE.Vector2(ri, y0),
  ];
  return mesh(new THREE.LatheGeometry(pts, segments), mat);
};

export const anchors = (g, a) => {
  g.userData.anchors = { ...(g.userData.anchors || {}), ...a };
  return g;
};

export const n = (v, d = 0) => (typeof v === "number" && isFinite(v) ? v : d);
